import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import MobilePageLayout from '../../../components/layout/MobilePageLayout';
import { theme } from '../../../styles/theme';
import { convertToVwMob } from '../../../Utils/responsive';
import { GIVING_ACCOUNT_MEMBER_TYPES, useGivingAccountApply } from '../useGivingAccountApply';

/** 고정 하단 취소/신청 바 높이 — 스크롤 Main에 패딩으로 넣어 푸터가 바에 가리지 않게 함 */
const MAIN_PADDING_BOTTOM = `calc(${convertToVwMob(16)} + ${convertToVwMob(50)} + ${convertToVwMob(20)} + ${convertToVwMob(16)} + env(safe-area-inset-bottom, 0px))`;

const GivingAccountApplyMob = () => {
  const { t } = useTranslation('translation');
  const {
    step,
    memberType,
    isOrganization,
    handleSelectMemberType,
    agreement,
    agreementChecks,
    toggleAgreementCheck,
    canGoNext,
    handleNext,
    applicantName,
    applicantEmail,
    documents,
    organization,
    setOrganization,
    organizationDesc,
    setOrganizationDesc,
    canSubmit,
    handleRegisterDocument,
    handleCancel,
    handleSubmit,
  } = useGivingAccountApply();

  // 1단계 유형 선택 → 2단계 설명·동의 → 3단계 정보 입력. 1·2단계는 "다음", 3단계는 "신청하기"
  const isLastStep = step === 3;
  const primaryLabel = isLastStep ? t('page.givingAccountApply.actions.submit') : t('common.actions.next');
  const primaryDisabled = isLastStep ? !canSubmit : !canGoNext;
  const handlePrimary = isLastStep ? handleSubmit : handleNext;

  return (
    <MobilePageLayout
      title={t('page.givingAccountApply.pageTitle')}
      showBottomNav={false}
      mainPaddingBottom={MAIN_PADDING_BOTTOM}
    >
      <PageContainer>
        <PageTitle>{t('page.givingAccountApply.pageTitle')}</PageTitle>
        <StepIndicator>{t('page.givingAccountApply.stepIndicator', { step, total: 3 })}</StepIndicator>

        <FieldList>
          {step === 1 && (
            <>
              <FieldGroup>
                <FieldTitle>{t('page.givingAccountApply.steps.type')}</FieldTitle>
                <RadioGroup role="radiogroup" $vertical>
                  {GIVING_ACCOUNT_MEMBER_TYPES.map((type) => (
                    <RadioItem
                      key={type}
                      role="radio"
                      aria-checked={memberType === type}
                      onClick={() => handleSelectMemberType(type)}
                    >
                      <RadioCircle active={memberType === type} />
                      <RadioLabel>{t(`page.givingAccountApply.accountTypes.${type}`)}</RadioLabel>
                    </RadioItem>
                  ))}
                </RadioGroup>
              </FieldGroup>
            </>
          )}

          {step === 2 && (
            <>
              {agreement && (
                <>
                  <FieldGroup>
                    <FieldTitle>{agreement.title}</FieldTitle>
                    <AgreementIntro>{agreement.intro}</AgreementIntro>
                  </FieldGroup>
                  <AgreementBox>
                    {agreement.sections.map((section) => (
                      <AgreementItem key={section.title}>
                        <AgreementItemTitle>{section.title}</AgreementItemTitle>
                        {section.body && <AgreementItemBody>{section.body}</AgreementItemBody>}
                        {section.items && (
                          <AgreementList>
                            {section.items.map((item) => (
                              <AgreementListItem key={item}>{item}</AgreementListItem>
                            ))}
                          </AgreementList>
                        )}
                        {section.note && <AgreementNote>{section.note}</AgreementNote>}
                      </AgreementItem>
                    ))}
                  </AgreementBox>
                  <FieldGroup>
                    <FieldTitle>{agreement.checksTitle}</FieldTitle>
                    {agreement.checks.map((check, index) => (
                      <CheckboxGroup
                        key={check}
                        role="checkbox"
                        aria-checked={!!agreementChecks[index]}
                        onClick={() => toggleAgreementCheck(index)}
                      >
                        <CheckboxSquare active={!!agreementChecks[index]} />
                        <CheckboxLabel>{check}</CheckboxLabel>
                      </CheckboxGroup>
                    ))}
                  </FieldGroup>
                </>
              )}
            </>
          )}

          {step === 3 && (
            <>
              <FieldGroup>
                <FieldTitle>{t('page.givingAccountApply.steps.form')}</FieldTitle>
                <InputBox disabled>
                  <InputLabel>{t('page.givingAccountApply.labels.memberType')}</InputLabel>
                  <InputValue>{memberType ? t(`page.givingAccountApply.accountTypes.${memberType}`) : '-'}</InputValue>
                </InputBox>
              </FieldGroup>

              <InputBox disabled>
                <InputLabel>{t('page.givingAccountApply.labels.applicantName')}</InputLabel>
                <InputValue>{applicantName || '-'}</InputValue>
              </InputBox>
              <InputBox disabled>
                <InputLabel>{t('page.givingAccountApply.labels.applicantEmail')}</InputLabel>
                <InputValue>{applicantEmail || '-'}</InputValue>
              </InputBox>

              <FieldGroup>
                <FieldTitle>{t('page.givingAccountApply.labels.documents')}</FieldTitle>
                {documents.map((doc) => (
                  <DocumentRow key={doc.key}>
                    <DocumentName>{t(`page.givingAccountApply.documents.${doc.key}`)}</DocumentName>
                    {doc.registered ? (
                      <DocumentRegistered>{t('page.givingAccountApply.documents.registered')}</DocumentRegistered>
                    ) : (
                      <DocumentRegisterButton type="button" onClick={() => handleRegisterDocument(doc.key)}>
                        {t('page.givingAccountApply.documents.register')}
                      </DocumentRegisterButton>
                    )}
                  </DocumentRow>
                ))}
              </FieldGroup>

              {isOrganization && (
                <>
                  <InputBox as="label">
                    <InputLabel as="span">{t('page.givingAccountApply.labels.organization')}</InputLabel>
                    <StyledInput
                      type="text"
                      value={organization}
                      maxLength={100}
                      placeholder={t('page.givingAccountApply.placeholders.organization')}
                      onChange={(e) => setOrganization(e.target.value)}
                    />
                  </InputBox>

                  <InputBox as="label">
                    <InputLabel as="span">{t('page.givingAccountApply.labels.organizationDesc')}</InputLabel>
                    <StyledTextArea
                      value={organizationDesc}
                      rows={5}
                      maxLength={2000}
                      placeholder={t('page.givingAccountApply.placeholders.organizationDesc')}
                      onChange={(e) => setOrganizationDesc(e.target.value)}
                    />
                  </InputBox>
                </>
              )}
            </>
          )}
        </FieldList>
      </PageContainer>

      <BottomButtonGroup>
        <ButtonRow>
          <Button type="button" onClick={handleCancel}>
            {t('common.actions.cancel')}
          </Button>
          <Button type="button" primary disabled={primaryDisabled} onClick={handlePrimary}>
            {primaryLabel}
          </Button>
        </ButtonRow>
      </BottomButtonGroup>
    </MobilePageLayout>
  );
};

/* 나의 계정(MyAccount) 모바일 화면과 같은 입력칸·버튼 스타일 */
const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(24)};
  padding: ${convertToVwMob(8)} ${convertToVwMob(0)} ${convertToVwMob(100)};
`;

const PageTitle = styled.h1`
  ${theme.typography.head.semiBold24};
  color: ${theme.colors.grayScale[90]};
  margin: 0;
`;

const StepIndicator = styled.p`
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[60]};
  margin: 0;
`;

const FieldList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(24)};
  width: 100%;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(12)};
  width: 100%;
`;

const FieldTitle = styled.p`
  ${theme.typography.contents.medium20};
  color: ${theme.colors.grayScale[90]};
  margin: 0;
`;

const InputBox = styled.div<{ disabled?: boolean }>`
  background-color: ${(props) => (props.disabled ? theme.colors.grayScale[10] : 'white')};
  border: 1px solid ${theme.colors.grayScale[20]};
  border-radius: ${convertToVwMob(4)};
  padding: ${convertToVwMob(8)} ${convertToVwMob(12)};
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(2)};
  width: 100%;
  box-sizing: border-box;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'text')};

  &:focus-within {
    border: 1.6px solid ${theme.colors.primaryBlue[40]};
  }
`;

const InputLabel = styled.label`
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[60]};
`;

const InputValue = styled.p`
  ${theme.typography.contents.regular24};
  color: ${theme.colors.grayScale[50]};
  margin: 0;
  overflow-wrap: anywhere;
`;

const StyledInput = styled.input`
  ${theme.typography.contents.regular24};
  color: ${theme.colors.grayScale[90]};
  border: none;
  background: transparent;
  outline: none;
  padding: 0;
  width: 100%;

  &::placeholder {
    color: ${theme.colors.grayScale[50]};
  }
`;

const StyledTextArea = styled.textarea`
  ${theme.typography.contents.regular20};
  font-family: inherit;
  color: ${theme.colors.grayScale[90]};
  border: none;
  background: transparent;
  outline: none;
  padding: 0;
  width: 100%;
  resize: vertical;
  min-height: ${convertToVwMob(150)};

  &::placeholder {
    color: ${theme.colors.grayScale[50]};
  }
`;

const RadioGroup = styled.div<{ $vertical?: boolean }>`
  display: flex;
  flex-direction: ${({ $vertical }) => ($vertical ? 'column' : 'row')};
  gap: ${({ $vertical }) => ($vertical ? convertToVwMob(16) : convertToVwMob(35))};
  align-items: ${({ $vertical }) => ($vertical ? 'flex-start' : 'center')};
`;

const RadioItem = styled.div`
  display: flex;
  gap: ${convertToVwMob(10)};
  align-items: center;
  cursor: pointer;
`;

const RadioCircle = styled.div<{ active?: boolean }>`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  border-radius: 50%;
  border: 1.5px solid #E5E5E5;
  background: #ffffff;
  position: relative;
  flex: 0 0 auto;

  &::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: ${convertToVwMob(12)};
    height: ${convertToVwMob(12)};
    border-radius: 50%;
    background: ${theme.colors.primaryBlue[40]};
    transform: ${({ active }) =>
      active ? 'translate(-50%, -50%) scale(1)' : 'translate(-50%, -50%) scale(0)'};
    transition: transform 0.15s ease;
  }
`;

const RadioLabel = styled.span`
  ${theme.typography.contents.regular20};
  color: ${theme.colors.grayScale[90]};
`;

const DocumentRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${convertToVwMob(12)};
  border: 1px solid ${theme.colors.grayScale[20]};
  border-radius: ${convertToVwMob(4)};
  padding: ${convertToVwMob(12)};
  min-height: ${convertToVwMob(62)};
  box-sizing: border-box;
`;

const DocumentName = styled.span`
  ${theme.typography.contents.regular20};
  color: ${theme.colors.grayScale[90]};
`;

const DocumentRegistered = styled.span`
  ${theme.typography.contents.medium20};
  color: ${theme.colors.primaryBlue[40]};
  white-space: nowrap;
`;

const DocumentRegisterButton = styled.button`
  ${theme.typography.contents.regular20};
  border: 1px solid #DFDFDF;
  border-radius: ${convertToVwMob(5)};
  background: #ffffff;
  color: #676767;
  padding: ${convertToVwMob(4)} ${convertToVwMob(20)};
  white-space: nowrap;
  cursor: pointer;
`;

const AgreementIntro = styled.p`
  ${theme.typography.contents.regular20};
  color: ${theme.colors.grayScale[70]};
  margin: 0;
  white-space: pre-line;
`;

const AgreementBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(20)};
  border: 1px solid ${theme.colors.grayScale[20]};
  border-radius: ${convertToVwMob(4)};
  background: ${theme.colors.grayScale[10]};
  padding: ${convertToVwMob(20)};
  box-sizing: border-box;
`;

const AgreementItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${convertToVwMob(6)};
`;

const AgreementItemTitle = styled.p`
  ${theme.typography.contents.medium20};
  color: ${theme.colors.grayScale[90]};
  margin: 0;
`;

const AgreementItemBody = styled.p`
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[70]};
  margin: 0;
  white-space: pre-line;
  line-height: 160%;
`;

const AgreementList = styled.ul`
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[70]};
  margin: 0;
  padding-left: ${convertToVwMob(20)};
  line-height: 160%;
`;

const AgreementListItem = styled.li`
  margin: 0;
`;

const AgreementNote = styled.p`
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[60]};
  margin: 0;
  white-space: pre-line;
  line-height: 160%;
`;

/* 나의 계정 화면과 같은 체크박스 스타일 */
const CheckboxGroup = styled.div`
  display: flex;
  gap: ${convertToVwMob(12)};
  align-items: flex-start;
  cursor: pointer;
  min-width: 0;
`;

const CheckboxSquare = styled.div<{ active?: boolean }>`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  flex: 0 0 ${convertToVwMob(20)};
  margin-top: ${convertToVwMob(5)};
  border: 1px solid ${(props) => (props.active ? theme.colors.primaryBlue[40] : theme.colors.grayScale[40])};
  border-radius: ${convertToVwMob(2)};
  background-color: ${(props) => (props.active ? theme.colors.primaryBlue[40] : 'white')};
  display: flex;
  align-items: center;
  justify-content: center;

  ${(props) =>
    props.active &&
    `
    &::after {
      content: '✓';
      color: white;
      font-size: ${convertToVwMob(14)};
    }
  `}
`;

const CheckboxLabel = styled.span`
  ${theme.typography.contents.regular20};
  color: ${theme.colors.grayScale[90]};
  min-width: 0;
  overflow-wrap: anywhere;
`;

const BottomButtonGroup = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: white;
  border-top: 0.5px solid ${theme.colors.grayScale[40]};
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: ${convertToVwMob(16)} ${convertToVwMob(32)};
  z-index: 1100;
  padding-bottom: calc(${convertToVwMob(32)} + env(safe-area-inset-bottom));
`;

const ButtonRow = styled.div`
  display: flex;
  gap: ${convertToVwMob(16)};
  align-items: center;
`;

const Button = styled.button<{ primary?: boolean }>`
  width: ${convertToVwMob(130)};
  height: ${convertToVwMob(54)};
  padding: ${convertToVwMob(10)};
  flex-shrink: 0;
  border-radius: ${convertToVwMob(5)};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-family: Pretendard;
  font-size: ${convertToVwMob(20)};
  font-weight: 400;
  line-height: 150%;

  ${(props) =>
    props.primary
      ? `
    background-color: ${theme.colors.primaryBlue[80]};
    color: white;
    border: none;
  `
      : `
    background-color: white;
    color: #676767;
    border: 1px solid #DFDFDF;
  `}

  &:disabled {
    cursor: not-allowed;
    background-color: #DFDFDF;
    color: #BCBCBC;
  }
`;

export default GivingAccountApplyMob;
